'use strict';

const libWorkspace = require('pict-section-workspace');
const libRoleDates = require('../access/RoleAssignment-Dates.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

// UserWorkspace — refactored onto pict-section-workspace. The base owns the shell (header, tab bar, routing,
// load); this subclass carries the bespoke User surfaces: an editable Overview, the three legacy user-admin
// actions (reset password, impersonate, edit), the Roles & Groups read panel, the Certifications editor, and
// the per-user Activity dashboard (embedded from ActivityObservability). The Projects tab is a plain
// association editor (base `assoc` kind, ProjectUser). Config in [[User.config.js]]. Mirrors [[ContractWorkspace-Provider.js]].

// User.IDRole is NOT a Roles FK — it is the base permission LEVEL (0-5). Canonical level names (mirrors the
// legacy user admin's role list: web/app_headlight/src/components/RoleSelector.vue / UserListPage.vue).
const USER_LEVEL_NAMES =
{
	0: 'Read Only',
	1: 'Field Personnel',
	2: 'Office Staff',
	3: 'Office Manager',
	4: 'Executive Manager',
	5: 'Pavia Administrator',
};

// Overview groups. `Edit` marks a field editable in edit mode ('text' | 'number' | 'bool'); the rest (and the
// Audit group) are read-only. Credential columns + SettingsJSON are intentionally not listed.
const FIELD_GROUPS =
[
	{ Name: 'Identity', Fields:
		[
			{ Key: 'NameFirst', Label: 'First Name', Edit: 'text' },
			{ Key: 'NameLast', Label: 'Last Name', Edit: 'text' },
			{ Key: 'Email', Label: 'Email', Wide: true, Edit: 'text' },
			{ Key: 'LoginID', Label: 'Login ID', Edit: 'text' },
			{ Key: 'Phone', Label: 'Phone', Edit: 'text' },
		] },
	{ Name: 'Role & Organization', Fields:
		[
			{ Key: 'IDRole', Label: 'Role', Level: true, Edit: 'number' },
			{ Key: 'Title', Label: 'Title', Edit: 'text' },
			{ Key: 'Classification', Label: 'Classification', Edit: 'text' },
			{ Key: 'IDCustomer', Label: 'Customer', Card: 'Customer', Edit: 'number' },
			{ Key: 'IDProjectOffice', Label: 'Project Office', Card: 'ProjectOffice', Edit: 'number' },
		] },
	{ Name: 'Status', Fields:
		[
			{ Key: 'Disabled', Label: 'Disabled', Format: 'bool', Edit: 'bool' },
			{ Key: 'EmailPending', Label: 'Email Pending', Format: 'bool', Edit: 'bool' },
			{ Key: 'Timezone', Label: 'Timezone', Edit: 'text' },
			{ Key: 'Shift', Label: 'Shift', Edit: 'text' },
			{ Key: 'LastLoginTime', Label: 'Last Login', Format: 'date' },
		] },
	{ Name: 'Audit', Fields:
		[
			{ Key: 'CreateDate', Label: 'Created', Format: 'date' },
			{ Key: 'UpdateDate', Label: 'Last Updated', Format: 'date' },
			{ Key: 'GUIDUser', Label: 'GUID', Mono: true },
			{ Key: 'IDUser', Label: 'User ID', Format: 'id' },
		] },
];

class UserWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._user = null;
		this._pwInputEl = null;
		this._editing = false;
		this._edit = null;
		this._savingEdit = false;
		this._relatedDisplay = {};
		this._roleScopes = null;
		this._rolesLoading = false;
		this._rolesError = '';
		this._rolesLoadedFor = null;
		this._certs = null;
		this._certsLoading = false;
		this._certsError = '';
		this._certsLoadedFor = null;
		this._certTypeNames = {};
		this._certAdd = { On: '', Start: '', End: '', Notes: '' };
		this._certBusy = false;
		this._certNotice = '';
		this._rolesBusy = false;
		this._rolesNotice = '';
	}

	get currentUser() { return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {}; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); } }
	get _prefix() { return (this.restClient && this.restClient.options && this.restClient.options.ServerURL) || '/1.0/'; }
	_deleteEntity(pEntity, pID) { return new Promise((resolve) => { if (this.restClient && (typeof this.restClient.deleteEntity === 'function')) { this.restClient.deleteEntity(pEntity, pID, (pError) => resolve(!pError)); } else { resolve(false); } }); }
	_escape(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	/** The base has no `_render`; the ported methods below call it — route it to a full workspace repaint. */
	_render() { this.renderWorkspace(); }
	/** The certification picker binds to AppData.UserWorkspace.CertAddTypeID; base `state` IS AppData.UserWorkspace. */
	get workspaceState() { return this.state; }
	get idUser() { return this._user ? this._user.IDUser : null; }

	// --- hydrate (runs inside the base load, after the User record arrives) ---
	async hydrateAsync(pRecord)
	{
		this._user = pRecord;
		this._editing = false; this._edit = null; this._savingEdit = false;
		this._roleScopes = null; this._rolesLoadedFor = null; this._rolesError = ''; this._rolesLoading = false;
		this._certs = null; this._certsLoadedFor = null; this._certsError = ''; this._certsLoading = false;
		this._certAdd = { On: '', Start: '', End: '', Notes: '' };
		this.state.CertAddTypeID = '';
		this._relatedDisplay = {};
		this._resolveRelatedDisplayAsync(pRecord);
		this._rolesBusy = false; this._rolesNotice = '';
		this.state.RoleAddID = ''; this.state.RoleAddScope = 'Customer'; this.state.RoleAddContextID = ''; this.state.RoleAddExpires = ''; this.state.RoleAddStarts = '';
	}

	// --- Permissions (mirror the server gates) ---
	get canResetPassword()
	{
		if (!this._user) { return false; }
		const tmpSession = this.currentUser;
		if (Number(tmpSession.IDUser) === Number(this._user.IDUser)) { return true; }
		return Number(tmpSession.UserRoleIndex) > Number(this._user.IDRole);
	}
	get canImpersonate()
	{
		if (!this._user) { return false; }
		const tmpSession = this.currentUser;
		if (Number(tmpSession.IDUser) === Number(this._user.IDUser)) { return false; }
		return Number(tmpSession.UserRoleIndex) >= 5;
	}
	get canEditUser()
	{
		if (!this._user) { return false; }
		const tmpSession = this.currentUser;
		return (Number(tmpSession.IDUser) === Number(this._user.IDUser)) || (Number(tmpSession.UserRoleIndex) >= 4);
	}
	/** Real hash-precise gate (mirrors ProjectStaffing). Fail-OPEN before the capability map loads; the server is
	 *  authoritative (both grant + remove check User-Role-Associate — there is no User-Role-Disassociate hash). */
	_can(pEntity, pAction)
	{
		const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
		if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function')) { return tmpApp.EffectivePermissionsService.can(pEntity, pAction); }
		return true;
	}
	get canManageRoles() { return !!this._user && this._can('User-Role', 'Associate'); }
	/** Raw DELETE (the base only has _deleteEntity(entity,id)); Entity/<id> paths go through it, custom paths direct. */
	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			const tmpMatch = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (tmpMatch) { this.restClient.deleteEntity(tmpMatch[1], tmpMatch[2], (pError, pBody) => pError ? reject(pError) : resolve(pBody)); return; }
			fetch(`${this._prefix}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((pResponse) =>
				{
					if (!pResponse.ok) { reject(new Error(`HTTP ${pResponse.status}`)); return; }
					pResponse.text().then((pText) => resolve(pText ? JSON.parse(pText) : {})).catch(() => resolve({}));
				})
				.catch(reject);
		});
	}

	// --- Overview (bespoke render: read or inline-edit) ---
	_fmtField(pField, pValue)
	{
		if (pField.Format === 'bool')
		{
			if (pValue == null || pValue === '') { return '—'; }
			const tmpTruthy = (pValue === true) || (pValue === 1) || (pValue === '1') || (String(pValue).toLowerCase() === 'true');
			return tmpTruthy ? 'Yes' : 'No';
		}
		if (pValue == null || pValue === '') { return '—'; }
		switch (pField.Format)
		{
		case 'number': { const tmpN = Number(pValue); return Number.isFinite(tmpN) ? tmpN.toLocaleString('en-US') : String(pValue); }
		case 'id': { const tmpN = Number(pValue); return Number.isFinite(tmpN) ? String(Math.trunc(tmpN)) : String(pValue); }
		case 'date':
		{
			const tmpString = String(pValue);
			if (tmpString.indexOf('0000-00-00') === 0) { return '—'; }
			const tmpDate = new Date(tmpString);
			return Number.isNaN(tmpDate.getTime()) ? tmpString : tmpDate.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
		}
		default: return String(pValue);
		}
	}

	/** Read-mode display string for an Overview field: resolved level name, resolved FK name (0 -> em-dash), else formatted. */
	_displayValue(pField)
	{
		const tmpRaw = this._user ? this._user[pField.Key] : null;
		if (pField.Level)
		{
			if ((tmpRaw == null) || (tmpRaw === '')) { return '—'; }
			return USER_LEVEL_NAMES[Number(tmpRaw)] || `Level ${tmpRaw}`;
		}
		if (pField.Card)
		{
			if ((tmpRaw == null) || (Number(tmpRaw) <= 0)) { return '—'; }
			return (this._relatedDisplay && this._relatedDisplay[pField.Key]) || String(Math.trunc(Number(tmpRaw)));
		}
		return this._fmtField(pField, tmpRaw);
	}

	/**
	 * Resolve display names for the user's related entities (Customer, Project Office) so the Overview
	 * "Role & Organization" fields render names rather than raw ids. Best-effort; re-renders when names
	 * arrive, and unresolved targets fall back to the id. (IDRole is a static level, resolved inline.)
	 */
	async _resolveRelatedDisplayAsync(pUser)
	{
		if (!pUser) { return; }
		const tmpTargets = [];
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			const tmpID = pUser[pField.Key];
			if (pField.Card && (tmpID != null) && (Number(tmpID) > 0)) { tmpTargets.push({ Key: pField.Key, RecordSet: pField.Card, ID: tmpID }); }
		}));
		if (!tmpTargets.length) { return; }
		const tmpDisplay = {};
		await Promise.all(tmpTargets.map(async (pTarget) =>
		{
			try
			{
				const tmpRecord = await this._getJSON(`${pTarget.RecordSet}/${pTarget.ID}`);
				if (tmpRecord && !tmpRecord.Error) { tmpDisplay[pTarget.Key] = tmpRecord.Name || tmpRecord.DisplayName || tmpRecord.Title || String(pTarget.ID); }
			}
			catch (pError) { /* leave unresolved -> the field falls back to the id */ }
		}));
		this._relatedDisplay = Object.assign({}, this._relatedDisplay, tmpDisplay);
		if (this._user && (Number(this._user.IDUser) === Number(pUser.IDUser))) { this._render(); }
	}
	_isTruthy(pValue) { return (pValue === true) || (pValue === 1) || (pValue === '1') || (String(pValue).toLowerCase() === 'true'); }

	renderOverview()
	{
		if (!this._user) { return ''; }
		return FIELD_GROUPS.map((pGroup) =>
		{
			const tmpFields = pGroup.Fields.map((pField) =>
			{
				const tmpEditable = this._editing && (pField.Edit === 'text' || pField.Edit === 'number' || pField.Edit === 'bool');
				const tmpWide = pField.Wide ? ' pw-field-wide' : '';
				let tmpValHTML;
				if (!tmpEditable)
				{
					const tmpMono = pField.Mono ? ' pw-field-mono' : '';
					tmpValHTML = `<span class="pw-field-value${tmpMono}">${this._esc(this._displayValue(pField))}</span>`;
				}
				else if (pField.Edit === 'bool')
				{
					tmpValHTML = `<label class="uw-check"><input type="checkbox" ${this._isTruthy(this._edit[pField.Key]) ? 'checked' : ''} onchange="_Pict.providers.UserWorkspace.editField('${pField.Key}', this.checked)" /></label>`;
				}
				else
				{
					const tmpRaw = (this._edit && this._edit[pField.Key] != null) ? this._edit[pField.Key] : '';
					tmpValHTML = `<input class="input uw-edit-input" type="${pField.Edit === 'number' ? 'number' : 'text'}" value="${this._escape(tmpRaw)}" oninput="_Pict.providers.UserWorkspace.editField('${pField.Key}', this.value)" />`;
				}
				return `<div class="pw-field${tmpWide}"><span class="pw-field-label">${this._esc(pField.Label)}</span>${tmpValHTML}</div>`;
			}).join('');
			return `<div class="pw-group"><div class="pw-group-head">${this._esc(pGroup.Name)}</div><div class="pw-grid">${tmpFields}</div></div>`;
		}).join('');
	}

	startEdit() { if (!this._user || !this.canEditUser) { return; } this._editing = true; this._edit = Object.assign({}, this._user); this.renderWorkspace(); }
	cancelEdit() { this._editing = false; this._edit = null; this.renderWorkspace(); }
	editField(pKey, pValue) { if (!this._edit) { return; } this._edit[pKey] = pValue; }

	async saveAsync()
	{
		if (!this._user || !this._edit || this._savingEdit) { return; }
		this._savingEdit = true;
		this.renderWorkspace();
		const tmpPayload = { IDUser: this._user.IDUser };
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			if (pField.Edit === 'text') { tmpPayload[pField.Key] = (this._edit[pField.Key] == null) ? '' : String(this._edit[pField.Key]); }
			else if (pField.Edit === 'number') { const tmpNumber = Number(this._edit[pField.Key]); tmpPayload[pField.Key] = Number.isFinite(tmpNumber) ? tmpNumber : 0; }
			else if (pField.Edit === 'bool') { tmpPayload[pField.Key] = this._isTruthy(this._edit[pField.Key]) ? 1 : 0; }
		}));
		const tmpResult = await this._putJSON('User', tmpPayload);
		this._savingEdit = false;
		if (tmpResult && tmpResult.error)
		{
			this._toast('Could not save the user (you may not have permission to edit this user).', 'error');
			this.renderWorkspace();
			return;
		}
		this._user = Object.assign({}, this._user, tmpPayload);
		this._record = this._user;
		this._editing = false;
		this._edit = null;
		this.renderWorkspace();
		this._toast('User saved.', 'success');
	}

	// --- Reset password ---
	resetPasswordPrompt()
	{
		if (!this._user || !this.canResetPassword) { return; }
		if (!this.modal || (typeof this.modal.show !== 'function')) { this._toast('The modal service is unavailable.', 'error'); return; }
		const tmpName = `${this._user.NameFirst || ''} ${this._user.NameLast || ''}`.trim() || this._user.LoginID || `User ${this._user.IDUser}`;
		const tmpContent =
			`<p style="margin:0 0 0.75rem; color: var(--theme-color-text-secondary, #45505f);">Set a new password for <strong>${this._escape(tmpName)}</strong>. They can change it after their next sign-in.</p>`
			+ `<div class="field"><label class="label" style="font-size:0.8rem;">New password</label>`
			+ `<div class="control" style="display:flex; gap:0.5rem;">`
			+ `<input id="UW-NewPassword" class="input" type="text" autocomplete="new-password" placeholder="Enter or generate a password" style="font-family: ui-monospace, SFMono-Regular, Menlo, monospace;" />`
			+ `<button type="button" class="button is-light" onclick="_Pict.providers.UserWorkspace.fillGeneratedPassword()">Generate</button>`
			+ `</div></div>`;
		this._pwInputEl = null;
		this.modal.show(
			{
				title: 'Reset password', content: tmpContent, closeable: true,
				buttons: [ { Hash: 'cancel', Label: 'Cancel', Style: 'light' }, { Hash: 'reset', Label: 'Reset password', Style: 'link' } ],
				onOpen: (pDialog) => { this._pwInputEl = pDialog ? pDialog.querySelector('#UW-NewPassword') : null; if (this._pwInputEl) { this._pwInputEl.focus(); } },
			}).then((pHash) =>
			{
				if (pHash !== 'reset') { return; }
				const tmpPassword = this._pwInputEl ? String(this._pwInputEl.value || '') : '';
				this._pwInputEl = null;
				if (tmpPassword.length < 2) { this._toast('Enter a password of at least 2 characters.', 'error'); return; }
				this._submitPasswordAsync(tmpPassword);
			});
	}

	fillGeneratedPassword()
	{
		if (!this._pwInputEl) { return; }
		const tmpCharset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
		let tmpPassword = '';
		for (let i = 0; i < 12; i++) { tmpPassword += tmpCharset.charAt(Math.floor(Math.random() * tmpCharset.length)); }
		this._pwInputEl.value = tmpPassword;
		this._pwInputEl.focus();
	}

	async _submitPasswordAsync(pNewPassword)
	{
		try
		{
			const tmpResponse = await fetch(`${this._prefix}Password`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ IDUser: this._user.IDUser, NewPassword: pNewPassword }) });
			let tmpBody = null;
			try { tmpBody = await tmpResponse.json(); } catch (pParseError) { tmpBody = null; }
			if (!tmpResponse.ok || (tmpBody && tmpBody.Success === false)) { throw new Error((tmpBody && tmpBody.Error) ? tmpBody.Error : `server returned ${tmpResponse.status}`); }
			this._toast('Password reset.', 'success');
		}
		catch (pError) { this._toast(`Could not reset the password: ${pError.message || pError}`, 'error'); }
	}

	// --- Impersonate ---
	impersonatePrompt()
	{
		if (!this._user || !this.canImpersonate) { return; }
		const tmpName = `${this._user.NameFirst || ''} ${this._user.NameLast || ''}`.trim() || this._user.LoginID || `User ${this._user.IDUser}`;
		const fApply = () => this._impersonateAsync();
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(`Sign in as ${tmpName}? Your current session will be replaced by theirs until you sign out.`, { title: 'Impersonate user', confirmLabel: 'Impersonate', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fApply(); } });
		}
		else { fApply(); }
	}

	async _impersonateAsync()
	{
		try
		{
			const tmpResult = await this._getJSON(`Authentication/Impersonate/${this._user.IDUser}`);
			if (!tmpResult || tmpResult.Success === false) { throw new Error((tmpResult && tmpResult.Error) ? tmpResult.Error : 'the server declined the impersonation request.'); }
			this._toast(`Now signed in as ${this._user.LoginID || this._user.Email || ('user ' + this._user.IDUser)}. Reloading…`, 'success');
			if (typeof window !== 'undefined' && window.location) { window.location.reload(); }
		}
		catch (pError) { this._toast(`Could not impersonate: ${pError.message || pError}`, 'error'); }
	}

	// --- Roles & Groups tab (membership panel + grant/remove) ---
	renderRoles(pProv, pHost)
	{
		const tmpHelp = `<p class="pw-help" style="margin:0 0 0.9rem">Roles and permission groups this user is a member of, grouped by where each is assigned. Grant a role below, or remove a directly-assigned one; memberships inherited through a user group (“via …”) are managed from the composing group in Access &amp; Permissions.</p>`;
		this.pict.ContentAssignment.assignContent(pHost, `${tmpHelp}<div id="UW-RoleHost"></div>`);
		this._renderRoleHost();
	}

	_renderRoleHost()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#UW-RoleHost').length === 0) { return; }
		tmpCA.assignContent('#UW-RoleHost', this._roleHostHTML());
		this.mountRolePickers();
	}

	_roleHostHTML()
	{
		if (this._rolesLoading && !this._roleScopes) { return `<div class="pw-empty box has-text-grey has-text-centered"><span class="pw-spinner"></span><p class="mt-3">Loading roles…</p></div>${this._roleAddBlockHTML()}`; }
		if (this._rolesError && !this._roleScopes) { return `<div class="notification is-warning is-soft py-2 px-3">${this._esc(this._rolesError)}</div>${this._roleAddBlockHTML()}`; }
		const tmpScopes = this._roleScopes || [];
		const tmpListHTML = tmpScopes.length
			? tmpScopes.map((pScope) => `
				<div class="pw-group">
					<div class="pw-group-head-row"><span class="pw-group-head">${this._esc(pScope.ScopeName)}</span><span class="uw-role-count">${this._esc(pScope.CountLabel)}</span></div>
					<p class="uw-role-hint">${this._esc(pScope.ScopeHint)}</p>
					<div class="uw-role-list">${pScope.Roles.map((pRole) => this._roleRowHTML(pRole)).join('')}</div>
				</div>`).join('')
			: `<p class="has-text-grey">This user is not a member of any roles or permission groups.</p>`;
		return `${tmpListHTML}${this._roleAddBlockHTML()}`;
	}

	_roleRowHTML(pRole)
	{
		const tmpContexts = (pRole.ContextListSlot && pRole.ContextListSlot.length)
			? `<div class="uw-role-contexts">${pRole.ContextListSlot[0].Contexts.map((pContext) => this._roleContextHTML(pContext)).join('')}</div>` : '';
		// Customer-scope roles are the removable unit (no per-context row); the × sits on the role itself.
		const tmpRemove = (this.canManageRoles && pRole.RemoveSlot && pRole.RemoveSlot.length)
			? `<button class="uw-role-x" title="Remove this role from the user" onclick="_Pict.providers.UserWorkspace.removeRoleAsync(${Number(pRole.RemoveSlot[0].IDRole)}, '${this._esc(pRole.RemoveSlot[0].Context)}', ${Number(pRole.RemoveSlot[0].IDContext)})"><span class="icon"><i class="fas fa-xmark"></i></span></button>` : '';
		return `<div class="uw-role-row"><div class="uw-role-main"><span class="icon uw-role-icon"><i class="${this._esc(pRole.Icon)}"></i></span><span class="uw-role-name">${this._esc(pRole.Name)}</span><span class="tag ${this._esc(pRole.KindClass)}">${this._esc(pRole.KindLabel)}</span>${tmpRemove}</div>${tmpContexts}</div>`;
	}

	_roleContextHTML(pContext)
	{
		const tmpStart = (pContext.StartSlot && pContext.StartSlot.length) ? `<span class="tag is-light" title="This assignment starts">from ${this._esc(pContext.StartSlot[0].Text)}</span>` : '';
		const tmpExp = (pContext.ExpirationSlot && pContext.ExpirationSlot.length) ? `<span class="tag is-light" title="This assignment expires">until ${this._esc(pContext.ExpirationSlot[0].Text)}</span>` : '';
		const tmpState = (pContext.StateSlot && pContext.StateSlot.length) ? `<span class="uw-cert-badge ${this._esc(pContext.StateSlot[0].Class)}">${this._esc(pContext.StateSlot[0].Label)}</span>` : '';
		const tmpVia = (pContext.ViaSlot && pContext.ViaSlot.length) ? `<span class="tag is-warning is-light" title="Granted through a user group rather than assigned directly">via ${this._esc(pContext.ViaSlot[0].ViaLabel)}</span>` : '';
		// Direct (non-inherited) project/lab assignments get a per-context ×.
		const tmpRemove = (this.canManageRoles && pContext.RemoveSlot && pContext.RemoveSlot.length)
			? `<button class="uw-role-x" title="Remove this role on this ${this._esc(String(pContext.RemoveSlot[0].Context).toLowerCase())}" onclick="_Pict.providers.UserWorkspace.removeRoleAsync(${Number(pContext.RemoveSlot[0].IDRole)}, '${this._esc(pContext.RemoveSlot[0].Context)}', ${Number(pContext.RemoveSlot[0].IDContext)})"><span class="icon is-small"><i class="fas fa-xmark"></i></span></button>` : '';
		return `<div class="uw-role-context"><span class="icon is-small uw-role-context-icon"><i class="fas fa-angle-right"></i></span><span class="uw-role-context-name">${this._esc(pContext.Label)}</span><span class="uw-role-context-id">${this._esc(pContext.IDLabel)}</span>${tmpStart}${tmpExp}${tmpState}${tmpVia}${tmpRemove}</div>`;
	}

	async loadRolesAsync(pForce)
	{
		if (!this._user) { return; }
		const tmpIDUser = this._user.IDUser;
		if (this._rolesLoading) { return; }
		if (!pForce && this._rolesLoadedFor === tmpIDUser) { this._renderRoleHost(); return; }
		this._rolesLoading = true;
		this._rolesError = '';
		this._renderRoleHost();
		try
		{
			const tmpResponse = await this._getJSON(`Permissions/Roles/For/User/${tmpIDUser}/All/Contexts`);
			this._roleTimeZone = await this._loadRoleTimeZoneAsync('Customer', 0);
			this._roleContexts = (tmpResponse && Array.isArray(tmpResponse.Contexts)) ? tmpResponse.Contexts : [];
			const tmpNames = await this._resolveContextNames(tmpResponse);
			this._roleScopes = this._buildRoleScopes(tmpResponse, tmpNames);
			this._rolesLoadedFor = tmpIDUser;
		}
		catch (pError) { this._roleScopes = null; this._rolesError = `Could not load roles and groups: ${pError && pError.message ? pError.message : pError}`; }
		this._rolesLoading = false;
		this._renderRoleHost();
	}

	async _resolveContextNames(pResponse)
	{
		const tmpNames = { Project: {}, Lab: {} };
		const tmpContexts = (pResponse && Array.isArray(pResponse.Contexts)) ? pResponse.Contexts : [];
		const fCollect = (pContext) => Array.from(new Set(tmpContexts.filter((pEntry) => pEntry && pEntry.Context === pContext && Number(pEntry.IDContext) > 0).map((pEntry) => Number(pEntry.IDContext))));
		await Promise.all([ this._fillNames('Projects', 'IDProject', fCollect('Project'), tmpNames.Project), this._fillNames('Labs', 'IDLab', fCollect('Lab'), tmpNames.Lab) ]);
		return tmpNames;
	}

	async _fillNames(pEntitySet, pIDField, pIDs, pTarget)
	{
		if (!pIDs || !pIDs.length) { return; }
		try
		{
			const tmpRows = await this._getJSON(`${pEntitySet}/FilteredTo/FBL~${pIDField}~INN~${pIDs.join(',')}/0/${pIDs.length}`);
			(Array.isArray(tmpRows) ? tmpRows : []).forEach((pRow) => { if (pRow && pRow[pIDField] != null) { pTarget[pRow[pIDField]] = pRow.Name || pRow.Description || ''; } });
			// The batched list read is customer-filtered and excludes soft-deleted rows, so a cross-customer
			// view or a deleted project/lab resolves to no name and the row falls back to a bare id. Fill any
			// still-missing id from a single record-by-id GET (not list-scoped; same idiom as _roleResolveValue).
			const tmpEntity = pEntitySet.replace(/s$/, '');
			await Promise.all(pIDs.filter((pID) => !pTarget[pID]).map((pID) => this._getJSON(`${tmpEntity}/${pID}`)
				.then((pRow) => { if (pRow && pRow[pIDField] != null) { pTarget[pRow[pIDField]] = pRow.Name || pRow.Description || ''; } })
				.catch(() => {})));
		}
		catch (pError) { this.pict.log.warn(`UserWorkspace: could not resolve ${pEntitySet} names: ${pError && pError.message ? pError.message : pError}`); }
	}

	_buildRoleScopes(pResponse, pNames)
	{
		const tmpRolesByID = {};
		(pResponse && Array.isArray(pResponse.Roles) ? pResponse.Roles : []).forEach((pRole) => { if (pRole && pRole.IDRole != null) { tmpRolesByID[pRole.IDRole] = pRole; } });
		const tmpContexts = (pResponse && Array.isArray(pResponse.Contexts)) ? pResponse.Contexts : [];
		const tmpScopeOrder = [ 'Customer', 'Project', 'Lab' ];
		const tmpScopes = {};
		const fScope = (pContext) =>
		{
			const tmpKey = pContext || 'Customer';
			if (!tmpScopes[tmpKey]) { tmpScopes[tmpKey] = { Key: tmpKey, Meta: this._scopeMeta(tmpKey), RolesByID: {}, Order: [] }; if (tmpScopeOrder.indexOf(tmpKey) < 0) { tmpScopeOrder.push(tmpKey); } }
			return tmpScopes[tmpKey];
		};
		tmpContexts.forEach((pEntry) =>
		{
			if (!pEntry || pEntry.IDRole == null) { return; }
			const tmpRole = tmpRolesByID[pEntry.IDRole];
			if (!tmpRole) { return; }
			const tmpScope = fScope(pEntry.Context);
			let tmpRow = tmpScope.RolesByID[pEntry.IDRole];
			if (!tmpRow)
			{
				const tmpKind = this._roleKind(tmpRole.Type);
				tmpRow = { IDRole: pEntry.IDRole, Name: tmpRole.Name || `Role ${pEntry.IDRole}`, Icon: tmpKind.Icon, KindLabel: tmpKind.Label, KindClass: tmpKind.Class, Contexts: [], ContextSeen: {}, Direct: false, IDContext: 0 };
				tmpScope.RolesByID[pEntry.IDRole] = tmpRow;
				tmpScope.Order.push(pEntry.IDRole);
			}
			if (tmpScope.Key === 'Customer')
			{
				// Customer-wide: the role itself is the removable unit. Track whether it is held DIRECTLY (a
				// UserRoleJoin, removable) vs only inherited through a user group (SynthesizedIDRole → not removable).
				const tmpSynth = Number(pEntry.SynthesizedIDRole) || 0;
				const tmpInherited = tmpSynth && (tmpSynth !== Number(pEntry.IDRole));
				if (!tmpRow.IDContext) { tmpRow.IDContext = Number(pEntry.IDContext) || 0; }
				if (!tmpInherited) { tmpRow.Direct = true; tmpRow.IDContext = Number(pEntry.IDContext) || tmpRow.IDContext; }
			}
			else
			{
				const tmpIDContext = Number(pEntry.IDContext) || 0;
				if (!tmpRow.ContextSeen[tmpIDContext]) { tmpRow.ContextSeen[tmpIDContext] = true; tmpRow.Contexts.push(this._buildContextRow(tmpScope.Key, pEntry, tmpRolesByID, pNames)); }
			}
		});
		const tmpCustomerContext = Number((this._user && this._user.IDCustomer) || 0);
		return tmpScopeOrder
			.filter((pKey) => tmpScopes[pKey] && tmpScopes[pKey].Order.length)
			.map((pKey) =>
			{
				const tmpScope = tmpScopes[pKey];
				const tmpRoles = tmpScope.Order
					.map((pID) =>
					{
						const tmpRow = tmpScope.RolesByID[pID];
						const tmpContextRows = tmpRow.Contexts.sort((pA, pB) => String(pA.Label).localeCompare(String(pB.Label)));
						// Customer-scope: attach a remove target on the role when it is held directly.
						const tmpRemoveSlot = (tmpScope.Key === 'Customer' && tmpRow.Direct)
							? [ { IDRole: tmpRow.IDRole, Context: 'Customer', IDContext: tmpRow.IDContext || tmpCustomerContext } ] : [];
						return { Name: tmpRow.Name, Icon: tmpRow.Icon, KindLabel: tmpRow.KindLabel, KindClass: tmpRow.KindClass, ContextListSlot: tmpContextRows.length ? [ { Contexts: tmpContextRows } ] : [], RemoveSlot: tmpRemoveSlot };
					})
					.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
				return { ScopeName: tmpScope.Meta.Name, ScopeHint: tmpScope.Meta.Hint, CountLabel: String(tmpRoles.length), Roles: tmpRoles };
			});
	}

	_buildContextRow(pScopeKey, pEntry, pRolesByID, pNames)
	{
		const tmpIDContext = Number(pEntry.IDContext) || 0;
		const tmpNameMap = (pNames && pNames[pScopeKey]) || {};
		const tmpName = tmpNameMap[tmpIDContext];
		const tmpStart = libRoleDates.instantToDay(pEntry.StartDate, this._roleTimeZone);
		const tmpExpiration = libRoleDates.instantToDay(pEntry.ExpirationDate, this._roleTimeZone);
		const tmpState = libRoleDates.getWindowState(pEntry.StartDate, pEntry.ExpirationDate);
		const tmpStateSlot = [];
		if (tmpState === 'Scheduled')
		{
			tmpStateSlot.push({ Label: 'scheduled', Class: 'uw-cert-future' });
		}
		else if (tmpState === 'Expired')
		{
			tmpStateSlot.push({ Label: 'expired', Class: 'uw-cert-exp' });
		}
		let tmpVia = '';
		const tmpSynth = Number(pEntry.SynthesizedIDRole) || 0;
		const tmpInherited = !!(tmpSynth && (tmpSynth !== Number(pEntry.IDRole)));
		if (tmpInherited && pRolesByID[tmpSynth]) { tmpVia = pRolesByID[tmpSynth].Name || `Role ${tmpSynth}`; }
		// Direct project/lab assignment → removable; inherited "via group" → not removable.
		const tmpRemoveSlot = (!tmpInherited && tmpIDContext > 0)
			? [ { IDRole: Number(pEntry.IDRole), Context: pScopeKey, IDContext: tmpIDContext } ] : [];
		return { Label: tmpName || `${pScopeKey} ${tmpIDContext}`, IDLabel: tmpName ? `#${tmpIDContext}` : '', StartSlot: tmpStart ? [ { Text: tmpStart } ] : [], ExpirationSlot: tmpExpiration ? [ { Text: tmpExpiration } ] : [], StateSlot: tmpStateSlot, ViaSlot: tmpVia ? [ { ViaLabel: tmpVia } ] : [], RemoveSlot: tmpRemoveSlot };
	}

	_roleKind(pType)
	{
		if (pType === 'PermissionGroup') { return { Label: 'Permission Group', Icon: 'fas fa-layer-group', Class: 'is-primary is-light' }; }
		if (pType === 'UserGroup') { return { Label: 'User Group', Icon: 'fas fa-users', Class: 'is-warning is-light' }; }
		return { Label: 'Role', Icon: 'fas fa-user-shield', Class: 'is-light' };
	}
	_scopeMeta(pKey)
	{
		switch (pKey)
		{
		case 'Customer': return { Name: 'User level', Hint: 'Assigned customer-wide (via a User Role) — applies across all of this customer’s projects.' };
		case 'Project': return { Name: 'Project level', Hint: 'Assigned on specific projects.' };
		case 'Lab': return { Name: 'Lab level', Hint: 'Assigned on specific labs.' };
		default: return { Name: `${pKey} level`, Hint: `Assigned in the ${pKey} context.` };
		}
	}

	// --- Roles & Groups: the grant editor (add block + pickers + grant/remove) ---

	/** The customer whose roles/projects/labs are offered (the SUBJECT user's, not the session's — matters for super-user cross-customer edits). */
	_subjectCustomer() { return Number((this._user && this._user.IDCustomer) || (this.currentUser && (this.currentUser.CustomerID || this.currentUser.IDCustomer)) || 0); }

	/** Role picker source — roles AND permission/user groups (no Type filter), scoped to GLOBAL (0) + the subject
	 *  customer. The standard roles and all permission/user groups are seeded at IDCustomer 0, so an EQ on just the
	 *  customer dropped every one of them (the reported "limited set / no groups"); mirror AccessWorkspace's rail
	 *  which loads `INN~0,<customer>`. Groups are labelled so they're distinguishable from plain roles. */
	_roleDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Set: 'Roles', IDField: 'IDRole', PageSize: 50, Scope: () => `FBL~IDCustomer~INN~0,${this._subjectCustomer()}~FBV~Deleted~EQ~0`,
				ToOption: (pRole) => ({ Value: Number(pRole.IDRole), Text: (pRole.Name || `Role #${pRole.IDRole}`) + (pRole.Type === 'PermissionGroup' ? ' (permission group)' : (pRole.Type === 'UserGroup' ? ' (user group)' : '')), Record: pRole }),
			});
	}
	_roleResolveValue() { return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`Role/${tmpID}`).then((pRole) => ((pRole && pRole.IDRole) ? { Value: tmpID, Text: pRole.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })).catch(() => ({ Value: tmpID, Text: `#${tmpID}` })); }; }

	/** Project/Lab context picker source for a non-customer scope, scoped to the subject customer. */
	_contextDataProvider(pScope)
	{
		const tmpIsLab = (pScope === 'Lab');
		const tmpIDField = tmpIsLab ? 'IDLab' : 'IDProject';
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Set: tmpIsLab ? 'Labs' : 'Projects', IDField: tmpIDField, PageSize: 50, Scope: () => `FBV~IDCustomer~EQ~${this._subjectCustomer()}~FBV~Deleted~EQ~0`,
				ToOption: (pRow) => ({ Value: Number(pRow[tmpIDField]), Text: pRow.Name || `#${pRow[tmpIDField]}`, Record: pRow }),
			});
	}
	_contextResolveValue(pScope)
	{
		const tmpIsLab = (pScope === 'Lab');
		const tmpEntity = tmpIsLab ? 'Lab' : 'Project';
		const tmpIDField = tmpIsLab ? 'IDLab' : 'IDProject';
		return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`${tmpEntity}/${tmpID}`).then((pRow) => ((pRow && pRow[tmpIDField]) ? { Value: tmpID, Text: pRow.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })).catch(() => ({ Value: tmpID, Text: `#${tmpID}` })); };
	}

	_roleAddBlockHTML()
	{
		if (!this.canManageRoles) { return ''; }
		const tmpScope = this.state.RoleAddScope || 'Customer';
		const tmpNotice = this._rolesNotice ? `<div class="uw-cert-notice">${this._esc(this._rolesNotice)}</div>` : '';
		const tmpError = (this._rolesError && this._roleScopes) ? `<div class="uw-cert-error">${this._esc(this._rolesError)}</div>` : '';
		const tmpBusy = this._rolesBusy ? '<span class="uw-cert-busy">saving…</span>' : '';
		// Project/Lab-scoped grants can carry optional start and end days; Customer-wide cannot (the server rejects them).
		const tmpContextField = (tmpScope !== 'Customer')
			? `<div class="uw-role-field"><label class="uw-role-flabel">${tmpScope === 'Lab' ? 'Lab' : 'Project'}</label><div id="UW-RoleContextPicker" class="uw-cert-typepicker"></div></div>` : '';
		const tmpToday = this._escape(libRoleDates.todayIn(this._roleTimeZone));
		const tmpStartsField = (tmpScope !== 'Customer')
			? `<div class="uw-role-field"><label class="uw-role-flabel">Starts <span class="uw-role-opt">(optional)</span></label><input class="uw-role-date" type="date" min="${tmpToday}" value="${this._escape(this.state.RoleAddStarts || '')}" onchange="_Pict.providers.UserWorkspace.setRoleAddStarts(this.value)" /></div>` : '';
		const tmpExpiresField = (tmpScope !== 'Customer')
			? `<div class="uw-role-field"><label class="uw-role-flabel">Ends <span class="uw-role-opt">(optional)</span></label><input class="uw-role-date" type="date" min="${tmpToday}" value="${this._escape(this.state.RoleAddExpires || '')}" onchange="_Pict.providers.UserWorkspace.setRoleAddExpires(this.value)" /></div>` : '';
		return `<div class="uw-cert-add">
			<div class="uw-cert-add-head">Add a role</div>
			<div class="uw-role-add-row">
				<div class="uw-role-field"><label class="uw-role-flabel">Role or group</label><div id="UW-RolePicker" class="uw-cert-typepicker"></div></div>
				<div class="uw-role-field"><label class="uw-role-flabel">Scope</label>
					<div class="select"><select class="uw-role-scope" onchange="_Pict.providers.UserWorkspace.setRoleAddScope(this.value)">
						<option value="Customer"${tmpScope === 'Customer' ? ' selected' : ''}>Customer-wide</option>
						<option value="Project"${tmpScope === 'Project' ? ' selected' : ''}>Project</option>
						<option value="Lab"${tmpScope === 'Lab' ? ' selected' : ''}>Lab</option>
					</select></div>
				</div>
				${tmpContextField}
				${tmpStartsField}
				${tmpExpiresField}
			</div>
			<div class="uw-cert-add-actions"><button class="uw-cert-btn" onclick="_Pict.providers.UserWorkspace.addRoleAsync()">Add role</button>${tmpBusy}</div>
			${tmpNotice}${tmpError}
		</div>`;
	}

	mountRolePickers()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#UW-RolePicker').length > 0)
		{
			const tmpRP = tmpPicker.createPicker('UW-RolePicker',
				{
					DestinationAddress: '#UW-RolePicker', ValueAddress: 'AppData.UserWorkspace.RoleAddID',
					Searchable: true, AllowClear: true, ClearLabel: '— pick a role or group —', Placeholder: 'Search roles & groups…',
					DataProvider: this._roleDataProvider(), ResolveValue: this._roleResolveValue(),
				});
			if (tmpRP && (typeof tmpRP.render === 'function')) { tmpRP.render(); }
			if (tmpRP && (typeof tmpRP.setValue === 'function')) { tmpRP.setValue(this.state.RoleAddID || ''); }
		}
		const tmpScope = this.state.RoleAddScope || 'Customer';
		if ((tmpScope !== 'Customer') && (tmpCA.getElement('#UW-RoleContextPicker').length > 0))
		{
			const tmpLabel = (tmpScope === 'Lab') ? 'lab' : 'project';
			const tmpCP = tmpPicker.createPicker('UW-RoleContextPicker',
				{
					DestinationAddress: '#UW-RoleContextPicker', ValueAddress: 'AppData.UserWorkspace.RoleAddContextID',
					Searchable: true, AllowClear: true, ClearLabel: `— pick a ${tmpLabel} —`, Placeholder: `Search ${tmpLabel}s…`,
					DataProvider: this._contextDataProvider(tmpScope), ResolveValue: this._contextResolveValue(tmpScope),
				});
			if (tmpCP && (typeof tmpCP.render === 'function')) { tmpCP.render(); }
			if (tmpCP && (typeof tmpCP.setValue === 'function')) { tmpCP.setValue(this.state.RoleAddContextID || ''); }
		}
	}

	setRoleAddScope(pScope)
	{
		this.state.RoleAddScope = (pScope === 'Project' || pScope === 'Lab') ? pScope : 'Customer';
		this.state.RoleAddContextID = '';
		this.state.RoleAddExpires = '';
		this.state.RoleAddStarts = '';
		this._rolesError = ''; this._rolesNotice = '';
		this._renderRoleHost();
	}
	/** Stash the optional expiration WITHOUT a re-render so the date input keeps focus (mirrors setCertAddDate). */
	setRoleAddExpires(pValue) { this.state.RoleAddExpires = String(pValue || ''); }
	/** Stash the optional start day, likewise without a re-render. */
	setRoleAddStarts(pValue)
	{
		this.state.RoleAddStarts = String(pValue || '');
	}

	/**
	 * The time zone role days are read in: the project's when one is given, else the user's customer's, else
	 * the browser's ('').
	 * @param {String} pScope - Customer | Project | Lab
	 * @param {Number} pIDContext - the project or lab
	 * @return {Promise<String>} an IANA zone, or ''.
	 */
	async _loadRoleTimeZoneAsync(pScope, pIDContext)
	{
		const tmpSources = [];
		if ((pScope === 'Project') && pIDContext)
		{
			tmpSources.push(`Project/${Number(pIDContext)}`);
		}
		if (this._user && this._user.IDCustomer)
		{
			tmpSources.push(`Customer/${Number(this._user.IDCustomer)}`);
		}
		for (const tmpSource of tmpSources)
		{
			try
			{
				const tmpRecord = await this._getJSON(tmpSource);
				if (tmpRecord && libRoleDates.isValidTimeZone(tmpRecord.Timezone))
				{
					return tmpRecord.Timezone;
				}
			}
			catch (pError)
			{
				// Fall through to the next source; the browser's zone is the last resort.
			}
		}
		return '';
	}

	/**
	 * Why the drafted start and end days cannot be saved, if they cannot.
	 * @param {Number} pIDRole - the role being added
	 * @param {String} pScope - Project | Lab
	 * @param {Number} pIDContext - the project or lab
	 * @param {String} pTimeZone - the zone the days are read in
	 * @return {String} a message for the form, or ''.
	 */
	_checkRoleAddDays(pIDRole, pScope, pIDContext, pTimeZone)
	{
		const tmpStarts = this.state.RoleAddStarts;
		const tmpEnds = this.state.RoleAddExpires;
		if (tmpStarts && tmpEnds && (tmpStarts > tmpEnds))
		{
			return 'The start date has to be on or before the end date.';
		}
		if (tmpStarts && (tmpStarts > libRoleDates.todayIn(pTimeZone)))
		{
			// Adding a role the user already holds here updates that assignment, so a future start would
			// take the role away until then.
			const tmpHeld = (this._roleContexts || []).some((pEntry) =>
				(pEntry.Context === pScope) && (Number(pEntry.IDContext) === Number(pIDContext)) && (Number(pEntry.IDRole) === Number(pIDRole)) &&
				(libRoleDates.getWindowState(pEntry.StartDate, pEntry.ExpirationDate) === 'Active'));
			if (tmpHeld)
			{
				return 'This user already has this role there. A future start date would remove it until then. ' +
					'Leave the start date empty to change only the end date, or remove the role first.';
			}
		}
		return '';
	}

	async addRoleAsync()
	{
		if (!this._user || this._rolesBusy || !this.canManageRoles) { return; }
		const tmpIDRole = Number(this.state.RoleAddID) || 0;
		if (!tmpIDRole) { this._rolesError = 'Pick a role or group to add.'; this._rolesNotice = ''; this._renderRoleHost(); return; }
		const tmpScope = this.state.RoleAddScope || 'Customer';
		let tmpIDContext;
		if (tmpScope === 'Customer')
		{
			tmpIDContext = Number(this._user.IDCustomer) || 0;
			if (!tmpIDContext) { this._rolesError = 'This user has no customer set, so a customer-wide role cannot be granted.'; this._renderRoleHost(); return; }
		}
		else
		{
			tmpIDContext = Number(this.state.RoleAddContextID) || 0;
			if (!tmpIDContext) { this._rolesError = `Pick a ${tmpScope === 'Lab' ? 'lab' : 'project'} to scope the role to.`; this._renderRoleHost(); return; }
		}
		const tmpBody = { IDUser: Number(this._user.IDUser), IDRole: tmpIDRole, Context: tmpScope, IDContext: tmpIDContext };
		// Start and end days are only valid for non-Customer scopes (the server rejects them for Customer). Like
		// the #/Access RoleAssignment editor, they cover whole days in the project's or customer's zone.
		if (tmpScope !== 'Customer')
		{
			const tmpTimeZone = await this._loadRoleTimeZoneAsync(tmpScope, tmpIDContext);
			const tmpDayProblem = this._checkRoleAddDays(tmpIDRole, tmpScope, tmpIDContext, tmpTimeZone);
			if (tmpDayProblem)
			{
				this._rolesError = tmpDayProblem;
				this._rolesNotice = '';
				this._renderRoleHost();
				return;
			}
			if (this.state.RoleAddStarts)
			{
				tmpBody.StartDate = libRoleDates.dayStartToInstant(this.state.RoleAddStarts, tmpTimeZone);
			}
			if (this.state.RoleAddExpires)
			{
				tmpBody.ExpirationDate = libRoleDates.dayEndToInstant(this.state.RoleAddExpires, tmpTimeZone);
			}
		}
		this._rolesBusy = true; this._rolesError = ''; this._rolesNotice = '';
		this._renderRoleHost();
		const tmpRes = await this._putJSON('Permissions/Role/For/User', tmpBody);
		this._rolesBusy = false;
		if (tmpRes && tmpRes.error) { this._rolesError = `Could not add the role: ${(tmpRes.error && tmpRes.error.message) || tmpRes.error} (you may lack permission).`; this._renderRoleHost(); return; }
		this._rolesNotice = 'Role added.';
		this.state.RoleAddID = ''; this.state.RoleAddContextID = ''; this.state.RoleAddExpires = '';
		this.state.RoleAddStarts = '';
		this.loadRolesAsync(true);
	}

	async removeRoleAsync(pIDRole, pContext, pIDContext)
	{
		if (!this._user || this._rolesBusy || !this.canManageRoles) { return; }
		this._rolesBusy = true; this._rolesError = ''; this._rolesNotice = '';
		this._renderRoleHost();
		try
		{
			await this._deleteJSON(`Permissions/Role/${Number(pIDRole)}/For/User/${Number(this._user.IDUser)}/In/${pContext}/${Number(pIDContext)}`);
			this._rolesNotice = 'Role removed.';
		}
		catch (pError)
		{
			this._rolesBusy = false;
			this._rolesError = `Could not remove the role: ${pError && pError.message ? pError.message : pError}`;
			this._renderRoleHost();
			return;
		}
		this._rolesBusy = false;
		this.loadRolesAsync(true);
	}

	// --- Certifications tab (list / add / remove) ---
	renderCertifications(pProv, pHost)
	{
		this.pict.ContentAssignment.assignContent(pHost, `<p class="pw-help" style="margin:0 0 0.8rem">Certifications this user holds — the type, its validity dates, and an active/expired badge. Add a certification below (dates optional) or remove one.</p><div id="UW-CertHost"></div>`);
		this._renderCertHost();
	}

	_dateToISO(pYMD) { const tmpV = String(pYMD || '').trim(); return tmpV ? `${tmpV}T00:00:00.000Z` : null; }
	_isoToYMD(pISO) { const tmpV = String(pISO || ''); return (tmpV.length >= 10) ? tmpV.slice(0, 10) : ''; }

	async loadCertificationsAsync(pForce)
	{
		if (!this._user) { return; }
		const tmpIDUser = this._user.IDUser;
		if (this._certsLoading) { return; }
		if (!pForce && (this._certsLoadedFor === tmpIDUser)) { this._renderCertHost(); return; }
		this._certsLoading = true;
		this._certsError = '';
		this._renderCertHost();
		try
		{
			const tmpJoins = await this._getJSON(`CertificationEntityPolyJoins/FilteredTo/FBV~EntityType~EQ~User~FBV~IDEntity~EQ~${tmpIDUser}~FBV~Deleted~EQ~0/0/500`);
			const tmpJoinRows = Array.isArray(tmpJoins) ? tmpJoins : [];
			const tmpCertIDs = tmpJoinRows.map((pJoin) => Number(pJoin.IDCertification)).filter((pID) => pID > 0);
			const tmpCertByID = {};
			if (tmpCertIDs.length)
			{
				const tmpCerts = await this._getJSON(`Certifications/FilteredTo/FBL~IDCertification~INN~${tmpCertIDs.join(',')}~FBV~Deleted~EQ~0/0/${tmpCertIDs.length + 10}`);
				(Array.isArray(tmpCerts) ? tmpCerts : []).forEach((pCert) => { tmpCertByID[Number(pCert.IDCertification)] = pCert; });
			}
			this._certs = tmpJoinRows.map((pJoin) =>
			{
				const tmpCert = tmpCertByID[Number(pJoin.IDCertification)];
				if (!tmpCert) { return null; }
				return { IDCertification: Number(pJoin.IDCertification), IDJoin: Number(pJoin.IDCertificationEntityPolyJoin), IDCertificationType: Number(tmpCert.IDCertificationType), On: this._isoToYMD(tmpCert.CertifiedOn), Start: this._isoToYMD(tmpCert.CertifiedStartDate), End: this._isoToYMD(tmpCert.CertifiedEndDate) };
			}).filter((pRow) => pRow);
			this._certsLoadedFor = tmpIDUser;
			await this._resolveCertTypeNames();
		}
		catch (pError) { this._certsError = `Could not load certifications: ${pError && pError.message ? pError.message : pError}`; }
		this._certsLoading = false;
		this._renderCertHost();
	}

	async _resolveCertTypeNames()
	{
		const tmpIDs = Array.from(new Set((this._certs || []).map((pCert) => pCert.IDCertificationType).filter((pID) => (pID > 0) && !this._certTypeNames[pID])));
		if (!tmpIDs.length) { return; }
		const tmpRows = await this._getJSON(`CertificationTypes/FilteredTo/FBL~IDCertificationType~INN~${tmpIDs.join(',')}/0/${tmpIDs.length}`);
		(Array.isArray(tmpRows) ? tmpRows : []).forEach((pType) => { this._certTypeNames[Number(pType.IDCertificationType)] = pType.Name || pType.Hash || `Type ${pType.IDCertificationType}`; });
	}

	setCertAddDate(pField, pValue) { if (pField === 'On') { this._certAdd.On = pValue || ''; } else if (pField === 'Start') { this._certAdd.Start = pValue || ''; } else if (pField === 'End') { this._certAdd.End = pValue || ''; } }
	setCertAddNotes(pValue) { this._certAdd.Notes = String(pValue == null ? '' : pValue); }

	applyCertPreset(pYears)
	{
		const tmpBase = this._certAdd.Start || this._certAdd.On;
		if (!tmpBase) { this._certsError = 'Set a start date first.'; this._renderCertHost(); return; }
		const tmpParts = String(tmpBase).split('-').map((pPart) => Number(pPart));
		if (tmpParts.length !== 3) { return; }
		this._certAdd.End = `${tmpParts[0] + Number(pYears)}-${String(tmpParts[1]).padStart(2, '0')}-${String(tmpParts[2]).padStart(2, '0')}`;
		if (typeof document !== 'undefined') { const tmpEl = document.getElementById('uw-cert-end'); if (tmpEl) { tmpEl.value = this._certAdd.End; } }
	}

	async addCertificationAsync()
	{
		if (!this._user || this._certBusy) { return; }
		const tmpTypeID = Number(this.state.CertAddTypeID);
		if (!(tmpTypeID > 0)) { this._certsError = 'Pick a certification type to add.'; this._certNotice = ''; this._renderCertHost(); return; }
		if (this._certAdd.Start && this._certAdd.End && (this._certAdd.Start > this._certAdd.End)) { this._certsError = 'The end date is before the start date.'; this._renderCertHost(); return; }
		this._certBusy = true; this._certsError = ''; this._certNotice = '';
		this._renderCertHost();
		const tmpCertBody = { IDCertification: 0, IDCertificationType: tmpTypeID, Notes: String(this._certAdd.Notes || '') };
		const tmpOn = this._dateToISO(this._certAdd.On); if (tmpOn) { tmpCertBody.CertifiedOn = tmpOn; }
		const tmpStartISO = this._dateToISO(this._certAdd.Start); if (tmpStartISO) { tmpCertBody.CertifiedStartDate = tmpStartISO; }
		const tmpEndISO = this._dateToISO(this._certAdd.End); if (tmpEndISO) { tmpCertBody.CertifiedEndDate = tmpEndISO; }
		const tmpCert = await this._postJSON('Certification', tmpCertBody);
		let tmpOk = false;
		if (!tmpCert.error && tmpCert.result && tmpCert.result.IDCertification)
		{
			const tmpJoin = await this._postJSON('CertificationEntityPolyJoin', { IDCertificationEntityPolyJoin: 0, IDCertification: Number(tmpCert.result.IDCertification), EntityType: 'User', IDEntity: Number(this._user.IDUser) });
			tmpOk = !tmpJoin.error;
		}
		this._certBusy = false;
		if (!tmpOk) { this._certsError = 'The server rejected the certification (you may lack permission).'; this._renderCertHost(); return; }
		this._certNotice = 'Certification added.';
		this._certAdd = { On: '', Start: '', End: '', Notes: '' };
		this.state.CertAddTypeID = '';
		this.loadCertificationsAsync(true);
	}

	async removeCertificationAsync(pIDCertification, pIDJoin)
	{
		if (!this._user) { return; }
		await this._deleteEntity('CertificationEntityPolyJoin', Number(pIDJoin));
		await this._deleteEntity('Certification', Number(pIDCertification));
		this.loadCertificationsAsync(true);
	}

	_certStatusBadge(pCert)
	{
		if (!pCert.End && !pCert.Start) { return ''; }
		const tmpToday = new Date().toISOString().slice(0, 10);
		if (pCert.End && (pCert.End < tmpToday)) { return '<span class="uw-cert-badge uw-cert-exp">expired</span>'; }
		if (pCert.Start && (pCert.Start > tmpToday)) { return '<span class="uw-cert-badge uw-cert-future">not yet active</span>'; }
		return '<span class="uw-cert-badge uw-cert-active">active</span>';
	}

	_certTypeDataProvider()
	{
		const tmpPageSize = 25;
		const fMap = (pRows) => (Array.isArray(pRows) ? pRows : []).map((pType) => ({ Value: Number(pType.IDCertificationType), Text: (pType.Name || pType.Hash || `Type ${pType.IDCertificationType}`), Record: pType }));
		return (pTerm, pPage) =>
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			if (!tmpTerm)
			{
				const tmpStart = (Number(pPage) || 0) * tmpPageSize;
				return this._getJSON(`CertificationTypes/FilteredTo/FBV~Deleted~EQ~0~FSF~Hash~ASC~0/${tmpStart}/${tmpPageSize}`).then((pRows) => ({ results: fMap(pRows), hasMore: (Array.isArray(pRows) && (pRows.length >= tmpPageSize)) })).catch(() => ({ results: [], hasMore: false }));
			}
			const tmpLike = encodeURIComponent(`%${tmpTerm}%`);
			return Promise.all(
				[
					this._getJSON(`CertificationTypes/FilteredTo/FBV~Name~LK~${tmpLike}~FBV~Deleted~EQ~0/0/${tmpPageSize}`).catch(() => []),
					this._getJSON(`CertificationTypes/FilteredTo/FBV~Hash~LK~${tmpLike}~FBV~Deleted~EQ~0/0/${tmpPageSize}`).catch(() => []),
				]).then((pResults) =>
			{
				const tmpByID = {};
				pResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pType) => { const tmpID = Number(pType.IDCertificationType); if ((tmpID > 0) && !tmpByID[tmpID]) { tmpByID[tmpID] = pType; } }));
				return { results: fMap(Object.keys(tmpByID).map((pKey) => tmpByID[pKey])).slice(0, 50), hasMore: false };
			});
		};
	}

	mountCertPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#UW-CertTypePicker').length === 0) { return; }
		const tmpView = tmpPicker.createPicker('UW-CertTypePicker',
			{
				DestinationAddress: '#UW-CertTypePicker', ValueAddress: 'AppData.UserWorkspace.CertAddTypeID',
				Searchable: true, AllowClear: true, ClearLabel: '— pick a certification type —', Placeholder: 'Search certification types…',
				DataProvider: this._certTypeDataProvider(),
				ResolveValue: (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`CertificationType/${tmpID}`).then((pType) => ((pType && pType.IDCertificationType) ? { Value: tmpID, Text: (pType.Name || pType.Hash || `Type ${tmpID}`) } : { Value: tmpID, Text: `#${tmpID}` })).catch(() => ({ Value: tmpID, Text: `#${tmpID}` })); },
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	_renderCertHost()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#UW-CertHost').length === 0) { return; }
		tmpCA.assignContent('#UW-CertHost', this._certHostHTML());
		this.mountCertPicker();
	}

	_certHostHTML()
	{
		if (this._certsLoading && !this._certs) { return '<p class="pw-help">Loading…</p>'; }
		const tmpNotice = this._certNotice ? `<div class="uw-cert-notice">${this._esc(this._certNotice)}</div>` : '';
		const tmpError = this._certsError ? `<div class="uw-cert-error">${this._esc(this._certsError)}</div>` : '';
		const tmpBusy = this._certBusy ? '<span class="uw-cert-busy">saving…</span>' : '';
		const tmpRows = (this._certs || []).map((pCert) =>
		{
			const tmpName = this._certTypeNames[pCert.IDCertificationType] || `Type ${pCert.IDCertificationType}`;
			const tmpRange = (pCert.Start || pCert.End) ? `${pCert.Start || '—'} → ${pCert.End || '—'}` : 'no dates';
			return `<div class="uw-cert-row"><div class="uw-cert-main"><span class="uw-cert-name">${this._esc(tmpName)}</span>${this._certStatusBadge(pCert)}</div><div class="uw-cert-dates">${this._esc(tmpRange)}</div><button class="uw-cert-x" title="Remove" onclick="_Pict.providers.UserWorkspace.removeCertificationAsync(${pCert.IDCertification}, ${pCert.IDJoin})"><i class="fas fa-xmark"></i></button></div>`;
		}).join('');
		const tmpAdd = `<div class="uw-cert-add"><div class="uw-cert-add-head">Add a certification</div>`
			+ `<div id="UW-CertTypePicker" class="uw-cert-typepicker"></div>`
			+ `<div class="uw-cert-dates-row">`
			+ `<label><span>Certified on</span><input type="date" value="${this._esc(this._certAdd.On)}" onchange="_Pict.providers.UserWorkspace.setCertAddDate('On', this.value)"></label>`
			+ `<label><span>Start</span><input type="date" value="${this._esc(this._certAdd.Start)}" onchange="_Pict.providers.UserWorkspace.setCertAddDate('Start', this.value)"></label>`
			+ `<label><span>End</span><input id="uw-cert-end" type="date" value="${this._esc(this._certAdd.End)}" onchange="_Pict.providers.UserWorkspace.setCertAddDate('End', this.value)"></label>`
			+ `<span class="uw-cert-presets"><button onclick="_Pict.providers.UserWorkspace.applyCertPreset(1)">+1 yr</button><button onclick="_Pict.providers.UserWorkspace.applyCertPreset(5)">+5 yr</button></span>`
			+ `</div>`
			+ `<label class="uw-cert-notes"><span>Notes</span><input type="text" value="${this._esc(this._certAdd.Notes)}" placeholder="(optional)" onchange="_Pict.providers.UserWorkspace.setCertAddNotes(this.value)"></label>`
			+ `<div class="uw-cert-add-actions"><button class="uw-cert-btn" onclick="_Pict.providers.UserWorkspace.addCertificationAsync()">Add certification</button>${tmpBusy}</div></div>`;
		return `<div class="uw-cert-list">${tmpRows || '<p class="pw-help">No certifications yet.</p>'}</div>${tmpNotice}${tmpError}${tmpAdd}`;
	}

	// --- Activity tab (per-user Activity dashboard, embedded from ActivityObservability) ---
	renderActivity()
	{
		return `<p class="pw-help" style="margin:0 0 0.8rem">This user’s record-authoring activity over time — counts of the records they created (or last updated), by type.</p><div id="AO-UserActivity-Body"></div>`;
	}
	_loadActivity()
	{
		const tmpActivity = this.pict.providers.ActivityObservability;
		if (tmpActivity && (typeof tmpActivity.loadUserActivityAsync === 'function')) { tmpActivity.loadUserActivityAsync(this.idUser); }
	}
}

module.exports = UserWorkspaceProvider;
